import { useEffect, useRef, useState } from "react"
import {
  Button,
  Checkbox,
  FormField,
  Heading,
  Select,
  Text,
  TextField,
} from "@smooth/design-system-components"
import { DunningDialog } from "../../components/DunningDialog"
import { estimatedCost } from "../batch/AutoEvaluateOnSave"
import { evaluationError } from "../errors"
import { caseOrigin, inReleaseCheck } from "../origin"
import type { CaseOriginKind } from "../origin"
import { UNSET_SHORT_NAME, isUnsetTerminal } from "../../terminals"
import { PROGRESS_LABELS, caseProgress, isArchived, terminalGroupOrder } from "../types"
import type { CaseProgress, EvaluationCase, Terminals } from "../types"
import { AlertMessage, TerminalBadge } from "./parts"

/** チェックしたケースへのまとめての操作 */
type BulkAction = "generate" | "archive" | "restore" | "include" | "exclude"

/** 出どころの絞り込み。督促ケースから読み込んだもの・本番の下書きからのものは「本番から取り込んだ」にまとめる */
type OriginFilter = "all" | "standard" | "production" | "manual"
const ORIGIN_FILTERS: { value: OriginFilter; label: string }[] = [
  { value: "all", label: "すべての出どころ" },
  { value: "standard", label: "基本" },
  { value: "production", label: "本番から取り込んだ" },
  { value: "manual", label: "手で作った" },
]
const ORIGIN_FILTER_OF: Record<CaseOriginKind, OriginFilter> = {
  standard: "standard",
  production_draft: "production",
  dunning_case: "production",
  manual: "manual",
}

/** 生成状態の絞り込み */
type ProgressFilter = "all" | "not_generated" | "failed"
const PROGRESS_FILTERS: { value: ProgressFilter; label: string }[] = [
  { value: "all", label: "すべての生成状態" },
  { value: "not_generated", label: "未生成" },
  { value: "failed", label: "生成失敗" },
]

type Props = {
  /** 一覧を読み込み済みか。未取得の間は件数や空表示を出さない */
  loaded: boolean
  /** しまったケースも含む全ケース */
  cases: EvaluationCase[]
  terminals: Terminals
  selectedId: number | null
  disabled: boolean
  onChoose: (record: EvaluationCase) => void
  /** まとめて生成の途中のケース。行の状態を「生成中」にする */
  generatingIds?: ReadonlySet<number>
  /** チェックしたケースをまとめて生成する。渡したときだけ「生成（N件）」を出す */
  onGenerateMany?: (ids: number[]) => Promise<void>
  /** チェックしたケースをまとめてしまう（archived: true）／戻す（false）。渡したときだけ各行にチェックを出す */
  onArchiveMany?: (ids: number[], archived: boolean) => Promise<void>
  /** チェックしたケースをまとめて回帰テストに入れる（included: true）／外す（false） */
  onRegressionMany?: (ids: number[], included: boolean) => Promise<void>
}

const PROGRESS_TEXT_CLASS: Partial<Record<CaseProgress, string>> = {
  failed: "font-bold text-text-error",
}

/**
 * 検証ケースを期待する対応ごとにまとめ、名前と生成の状態を出す。しまったケースは「しまったケースも表示」にしたときだけ薄く出す。
 * 検索・出どころ・生成状態で絞り込み、チェックしたケース（表示中のものだけ）をまとめて生成・回帰テストに入れる／外す・しまう・戻す。
 * 回帰テスト（case_set core。プロンプトを変えるたびに流すケース）に入っている行には「回帰」の印を付ける
 */
export function CaseList({
  loaded,
  cases,
  terminals,
  selectedId,
  disabled,
  onChoose,
  generatingIds,
  onGenerateMany,
  onArchiveMany,
  onRegressionMany,
}: Props) {
  const [query, setQuery] = useState("")
  const [originFilter, setOriginFilter] = useState<OriginFilter>("all")
  const [progressFilter, setProgressFilter] = useState<ProgressFilter>("all")
  const [showArchived, setShowArchived] = useState(false)
  const [checked, setChecked] = useState<ReadonlySet<number>>(new Set())
  const [confirming, setConfirming] = useState<BulkAction | null>(null)
  const [working, setWorking] = useState(false)
  const [bulkError, setBulkError] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  // その他の操作のメニューは、外をクリック・Esc で閉じる
  useEffect(() => {
    if (!menuOpen) return
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false)
    }
    document.addEventListener("mousedown", close)
    document.addEventListener("keydown", escape)
    return () => {
      document.removeEventListener("mousedown", close)
      document.removeEventListener("keydown", escape)
    }
  }, [menuOpen])
  const active = cases.filter((record) => !isArchived(record))
  const archivedCount = cases.length - active.length
  // しまったケースが無くなったら切り替えを戻す（次にしまったときは隠れた状態から始める）
  if (loaded && showArchived && archivedCount === 0) setShowArchived(false)

  const filtered = (showArchived ? cases : active).filter(
    (record) =>
      `${record.title}\n${record.checkpoints}`.toLowerCase().includes(query.toLowerCase()) &&
      (originFilter === "all" || ORIGIN_FILTER_OF[caseOrigin(record).kind] === originFilter) &&
      (progressFilter === "all" || caseProgress(record) === progressFilter)
  )
  const groups = terminalGroupOrder(
    terminals,
    filtered.map((record) => record.expectedTerminal)
  )
    .map((id) => ({ id, records: filtered.filter((record) => record.expectedTerminal === id) }))
    .filter((group) => group.records.length > 0)

  // 操作するのは表示中のチェックしたケースだけ（検索・表示切替で隠れたものは含めない）
  const bulkEnabled = !!onArchiveMany || !!onGenerateMany || !!onRegressionMany
  const visibleChecked = filtered.filter((record) => checked.has(record.id))
  const activeChecked = visibleChecked.filter((record) => !isArchived(record))
  const activeIds = activeChecked.map((record) => record.id)
  const archivedIds = visibleChecked.filter(isArchived).map((record) => record.id)
  const includeIds = activeChecked
    .filter((record) => !inReleaseCheck(record) && !isUnsetTerminal(record.expectedTerminal))
    .map((record) => record.id)
  const excludeIds = activeChecked.filter(inReleaseCheck).map((record) => record.id)
  const allChecked = filtered.length > 0 && visibleChecked.length === filtered.length
  const targets =
    confirming === "restore"
      ? archivedIds
      : confirming === "include"
        ? includeIds
        : confirming === "exclude"
          ? excludeIds
          : activeIds
  const toggle = (id: number, on: boolean) =>
    setChecked((current) => {
      const next = new Set(current)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  const toggleAll = (on: boolean) =>
    setChecked((current) => {
      const next = new Set(current)
      filtered.forEach((record) => (on ? next.add(record.id) : next.delete(record.id)))
      return next
    })
  const ask = (action: BulkAction) => {
    setBulkError(null)
    setConfirming(action)
  }
  const confirm = async () => {
    if (!confirming) return
    setWorking(true)
    setBulkError(null)
    try {
      if (confirming === "generate") await onGenerateMany?.(targets)
      else if (confirming === "include" || confirming === "exclude")
        await onRegressionMany?.(targets, confirming === "include")
      else await onArchiveMany?.(targets, confirming === "archive")
      setChecked(new Set())
      setConfirming(null)
    } catch (e) {
      setBulkError(evaluationError(e))
    } finally {
      setWorking(false)
    }
  }
  const confirmQuestion: Record<BulkAction, string> = {
    generate: `生成しますか？（約 ${estimatedCost(targets.length)}）`,
    archive: "しまいますか？",
    restore: "戻しますか？",
    include: "回帰テストに入れますか？",
    exclude: "回帰テストから外しますか？",
  }
  const confirmTitle = confirming ? `${targets.length}件を${confirmQuestion[confirming]}` : ""
  const confirmLabel: Record<BulkAction, string> = {
    generate: "生成",
    archive: "しまう",
    restore: "戻す",
    include: "入れる",
    exclude: "外す",
  }

  // 「…」に出す操作（該当するケースがあるものだけ）
  const menuItems: { action: BulkAction; label: string }[] = [
    ...(onRegressionMany && includeIds.length > 0
      ? [{ action: "include" as const, label: `回帰テストに入れる（${includeIds.length}件）` }]
      : []),
    ...(onRegressionMany && excludeIds.length > 0
      ? [{ action: "exclude" as const, label: `回帰テストから外す（${excludeIds.length}件）` }]
      : []),
    ...(onArchiveMany && activeIds.length > 0
      ? [{ action: "archive" as const, label: `しまう（${activeIds.length}件）` }]
      : []),
    ...(onArchiveMany && archivedIds.length > 0
      ? [{ action: "restore" as const, label: `戻す（${archivedIds.length}件）` }]
      : []),
  ]

  return (
    <aside
      aria-label="検証ケース一覧"
      className="flex min-w-0 flex-col gap-4 border-b border-border-default py-5 min-[900px]:border-b-0 min-[900px]:border-r min-[900px]:pr-4"
    >
      <FormField label="ケースを検索">
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ケース名・メモで検索"
        />
      </FormField>
      <div className="grid grid-cols-2 gap-2">
        <Select
          aria-label="出どころ"
          value={originFilter}
          onChange={(e) => setOriginFilter(e.target.value as OriginFilter)}
          options={ORIGIN_FILTERS}
        />
        <Select
          aria-label="生成状態"
          value={progressFilter}
          onChange={(e) => setProgressFilter(e.target.value as ProgressFilter)}
          options={PROGRESS_FILTERS}
        />
      </div>
      {loaded && archivedCount > 0 && (
        <Checkbox
          label={`しまったケースも表示（${archivedCount}）`}
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
      )}
      {loaded && <Text size="sm">{filtered.length}件</Text>}
      {loaded && bulkEnabled && filtered.length > 0 && (
        // よく使う「生成」だけをボタンにし、ほかの操作（回帰テストへの出し入れ・しまう・戻す）は「…」にまとめる
        <div className="flex items-center gap-2">
          <Checkbox
            label="すべて選ぶ"
            checked={allChecked}
            disabled={disabled}
            onChange={(e) => toggleAll(e.target.checked)}
          />
          {visibleChecked.length > 0 && (
            <Text as="span" size="sm" tone="muted">
              {visibleChecked.length}件を選択中
            </Text>
          )}
          <span className="ml-auto flex items-center gap-2">
            {onGenerateMany && activeIds.length > 0 && (
              <Button variant="outline" disabled={disabled} onClick={() => ask("generate")}>
                生成
              </Button>
            )}
            {menuItems.length > 0 && (
              <div ref={menuRef} className="relative">
                <Button
                  variant="outline"
                  aria-label="選んだケースのその他の操作"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  disabled={disabled}
                  onClick={() => setMenuOpen((value) => !value)}
                >
                  …
                </Button>
                {menuOpen && (
                  <div
                    role="menu"
                    aria-label="選んだケースのその他の操作"
                    className="absolute right-0 top-full z-10 mt-1 w-60 rounded-md border border-border-default bg-bg-default py-1 shadow-card"
                  >
                    {menuItems.map((item) => (
                      <button
                        key={item.action}
                        type="button"
                        role="menuitem"
                        className="block w-full px-4 py-2.5 text-left text-body text-text-default hover:bg-bg-muted focus-visible:bg-bg-muted focus-visible:outline-none"
                        onClick={() => {
                          setMenuOpen(false)
                          ask(item.action)
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </span>
        </div>
      )}
      {/* 行のチェックボックスの見えない入力（absolute）がこの枠の外へはみ出してページを伸ばさないよう relative にする */}
      <div className="relative max-h-[240px] overflow-y-auto min-[900px]:max-h-[72vh]">
        {groups.map((group) => (
          <section key={group.id} aria-label={`対応 ${group.id}`}>
            <div className="sticky top-0 bg-bg-default pb-1.5 pt-3.5">
              <Heading level={3}>
                <span className="flex items-baseline gap-2">
                  <TerminalBadge id={group.id} />
                  {isUnsetTerminal(group.id) ? UNSET_SHORT_NAME : terminals[group.id]}
                </span>
              </Heading>
            </div>
            {group.records.map((record) => {
              const selected = selectedId === record.id
              const archived = isArchived(record)
              const state: CaseProgress = generatingIds?.has(record.id)
                ? "running"
                : caseProgress(record)
              const row = (
                <button
                  type="button"
                  aria-current={selected || undefined}
                  disabled={disabled}
                  onClick={() => onChoose(record)}
                  className={[
                    "flex w-full items-baseline gap-2.5 border-b border-l-[3px] border-b-border-default px-2.5 py-2.5 text-left text-body text-text-default",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring disabled:cursor-not-allowed",
                    selected
                      ? "border-l-action-primary bg-bg-info font-bold"
                      : "border-l-transparent hover:bg-bg-muted",
                    archived ? "opacity-60" : "",
                  ].join(" ")}
                >
                  <span className="min-w-0 flex-1">
                    {record.title}
                    {archived && (
                      <span className="block text-caption font-normal text-text-muted">
                        しまったケース
                      </span>
                    )}
                  </span>
                  {inReleaseCheck(record) && (
                    <span
                      aria-hidden
                      className="shrink-0 rounded-sm border border-border-default px-1 text-caption font-normal text-text-muted"
                    >
                      回帰
                    </span>
                  )}
                  <span
                    aria-hidden
                    className={`shrink-0 whitespace-nowrap text-caption ${PROGRESS_TEXT_CLASS[state] ?? ""}`}
                  >
                    {PROGRESS_LABELS[state]}
                  </span>
                </button>
              )
              if (!bulkEnabled) return <div key={record.id}>{row}</div>
              return (
                <div key={record.id} className="flex items-stretch">
                  <span className="flex shrink-0 items-center border-b border-border-default pl-1">
                    <Checkbox
                      label=""
                      aria-label={`${record.title}を選ぶ`}
                      checked={checked.has(record.id)}
                      disabled={disabled}
                      onChange={(e) => toggle(record.id, e.target.checked)}
                    />
                  </span>
                  <div className="min-w-0 flex-1">{row}</div>
                </div>
              )
            })}
          </section>
        ))}
        {loaded && filtered.length === 0 && (
          <Text>
            {cases.length
              ? "条件に一致するケースがありません。"
              : "標準ケースがまだありません。開発担当に反映を依頼してください。"}
          </Text>
        )}
      </div>
      {confirming && (
        <DunningDialog
          title={confirmTitle}
          onClose={() => !working && setConfirming(null)}
          footer={
            <>
              <Button variant="outline" disabled={working} onClick={() => setConfirming(null)}>
                キャンセル
              </Button>
              <Button variant="primary" disabled={working} onClick={confirm}>
                {confirmLabel[confirming]}
              </Button>
            </>
          }
        >
          {bulkError && <AlertMessage>{bulkError}</AlertMessage>}
        </DunningDialog>
      )}
    </aside>
  )
}
